import { useMemo, useCallback } from 'react';

// Define a type for property types
export type PropertyType = "Full Title" | "Sectional Title" | "Farm" | "Complex" | "Other";

// Function to convert text to title case
export const useToTitleCase = () => {
  return useMemo(() => (text: string): string => {
    if (!text) return '';
    
    return text
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, []);
};

// Function to get property type colors
export const usePropertyTypeColors = () => {
  // Memoize property type colors to prevent recreation on each render
  return useMemo(() => ({
    "Full Title": "#FF5733",
    "Sectional Title": "#33A1FF",
    "Farm": "#33FF57",
    "Complex": "#A233FF",
    "Other": "#FFD700"
  }), []);
};

// Function to get color for a property type with type safety
export const usePropertyTypeColor = () => {
  const propertyTypeColors = usePropertyTypeColors();
  
  return useMemo(() => (propertyType: string): string => {
    const validType = (propertyTypeColors.hasOwnProperty(propertyType) ? 
      propertyType : "Other") as PropertyType;
    return propertyTypeColors[validType];
  }, [propertyTypeColors]);
};

// Function to get single unit marker options
export const useSingleUnitMarkerOptions = () => {
  const getPropertyTypeColor = usePropertyTypeColor();
  
  return useMemo(() => (propertyType: string) => ({
    fillColor: getPropertyTypeColor(propertyType),
    fillOpacity: 0.8,
    strokeColor: "#000000",
    strokeOpacity: 0.8,
    strokeWeight: 0.1,
    zIndex: 1000,
    clickable: true,
    radius: 4
  }), [getPropertyTypeColor]);
};

// Function to get multi-unit marker style
export const useMultiUnitMarkerStyle = () => {
  const getPropertyTypeColor = usePropertyTypeColor();
  
  return useMemo(() => (propertyType: string) => ({
    backgroundColor: getPropertyTypeColor(propertyType)
  }), [getPropertyTypeColor]);
};

// Function to get Google Maps options - using the more comprehensive implementation from utils.tsx
export const useGoogleMapOptions = () => {
  const getGoogleMapOptions = useCallback((mapMode: 'pan' | 'marker' | 'polygon', mapId?: string) => {
    const options: google.maps.MapOptions = {
      mapId: mapId || undefined,
      disableDefaultUI: false,
      zoomControl: true,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: google.maps.ControlPosition.TOP_RIGHT
      },
      streetViewControl: true,
      rotateControl: false,
      fullscreenControl: true,
      gestureHandling: 'greedy',
      clickableIcons: false
    };

    // Only set styles if no mapId is present
    if (!mapId) {
      options.styles = [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ];
    }

    // Set cursor based on map mode
    if (mapMode === 'marker') {
      options.draggableCursor = 'crosshair';
    } else if (mapMode === 'polygon') {
      options.draggableCursor = 'crosshair'; // Same crosshair for polygon drawing
    } else {
      options.draggableCursor = 'grab';
      options.draggingCursor = 'grabbing';
    }

    return options;
  }, []);

  return getGoogleMapOptions;
};

// Define circle options that match our SCSS styles
export const useSearchCircleOptions = () => {
  return useMemo(() => ({
    fillColor: "#92ab1c",
    fillOpacity: 0.35,
    strokeColor: "#92ab1c",
    strokeOpacity: 0.8,
    strokeWeight: 1,
  }), []);
};

// Function to create property markers
export const createPropertyMarkers = (properties: any[]) => {
  // Filter invalid properties and group by location in one pass
  const locationGroups = properties.reduce((groups: Record<string, any[]>, property) => {
    if (!property.latitude || !property.longitude || 
        isNaN(property.latitude) || isNaN(property.longitude)) {
      return groups;
    }
    
    const lat = parseFloat(property.latitude);
    const lng = parseFloat(property.longitude);
    const locationKey = `${lat},${lng}`;
    
    if (!groups[locationKey]) {
      groups[locationKey] = [];
    }
    groups[locationKey].push({ ...property, lat, lng });
    return groups;
  }, {});

  // Create markers array
  return Object.values(locationGroups).map(propertiesAtLocation => {
    const sortedProperties = [...propertiesAtLocation].sort((a, b) => {
      const unitA = a.unitNumber || '';
      const unitB = b.unitNumber || '';
      return !isNaN(unitA) && !isNaN(unitB) ? 
        parseInt(unitA) - parseInt(unitB) : 
        unitA.localeCompare(unitB);
    });

    const propertiesWithUnits = sortedProperties.filter(p => p.unitNumber?.trim());
    const baseProperty = propertiesWithUnits[0] || sortedProperties[0];

    return {
      id: baseProperty.propertyId,
      position: { lat: baseProperty.lat, lng: baseProperty.lng },
      propertyType: baseProperty.propertyType || "Other",
      property: {
        ...baseProperty,
        ...(sortedProperties.length > 1 ? { multiUnitProperties: sortedProperties } : {})
      }
    };
  });
};

// Function to create legend items
export const createLegendItems = (properties: any[], getPropertyTypeColor: (type: string) => string) => {
  // Get unique property types from results
  const uniqueTypes = Array.from(new Set(properties.map(p => p.propertyType || "Other")));
  
  // Create legend items with colors
  return uniqueTypes.map(type => ({
    type: type as string,
    color: getPropertyTypeColor(type as string)
  }));
};

/**
 * Formats a property address according to the specified requirements:
 * UnitNumber + PropertyName + streetNumber + streetName + streetType
 * - If there is no unitNumber then use the Erf
 * - If the Erf and Street Number are the same, only use one
 * - Do not include property name when it is "Full Title", "Sectional Title", "Farm", "Complex"
 * 
 * @param property The property object containing address details
 * @returns Formatted address string in CamelCase
 */
export const formatPropertyAddress = (property: any): string => {
  if (!property) return '';
  
  // Check if property already has a formatted streetAddress
  if (property.streetAddress && typeof property.streetAddress === 'string') {
    // If we don't have the detailed components, return the existing address in CamelCase
    if (!property.streetNumber && !property.streetName && !property.unitNumber && !property.erf) {
      return property.streetAddress
        .toLowerCase()
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
  }
  
  // Extract property details
  const propertyType = property.propertyType || '';
  const propertyName = property.propertyName || '';
  const unitNumber = property.unitNumber || '';
  const erf = property.erf ? property.erf.toString() : '';
  const streetNumber = property.streetNumber || '';
  const streetName = property.streetName || '';
  const streetType = property.streetType || '';
  const suburb = property.suburb || '';
  
  // Build address components array
  const addressParts = [];
  
  // Add unit number
  if (unitNumber) {
    addressParts.push(unitNumber);
  }
  
  // Add property name if it's not one of the excluded types
  const excludedPropertyNames = ["Full Title", "Sectional Title", "Farm", "Complex"];
  if (propertyName && !excludedPropertyNames.includes(propertyName)) {
    addressParts.push(propertyName);
  }
  
  // Add street components
  if (streetNumber) {
    addressParts.push(streetNumber);
  }
  if (streetName) {
    addressParts.push(streetName);
  }
  if (streetType) {
    addressParts.push(streetType);
  }
  
  // Join all parts with appropriate separators
  let streetAddress = addressParts.join(' ');
  
  // Add suburb as fallback if address is empty
  if (!streetAddress) {
    streetAddress = property.suburb || property.province || '';
  }
  
  // Add Erf at the end if available
  if (erf) {
    streetAddress = streetAddress + (streetAddress ? ', Erf: ' + erf : 'Erf: ' + erf);
  }

  streetAddress = streetAddress + ', ' + suburb;
  
  // Convert to CamelCase
  return streetAddress
    .toLowerCase()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Checks if a point is inside a polygon
 * @param point The point to check
 * @param polygon The polygon to check against
 * @returns true if the point is inside the polygon, false otherwise
 */
export const isPointInPolygon = (
  point: google.maps.LatLng, 
  polygon: google.maps.Polygon
): boolean => {
  if (!polygon || !point) return false;
  
  const bounds = new google.maps.LatLngBounds();
  polygon.getPaths().forEach(path => {
    path.forEach(latLng => bounds.extend(latLng));
  });
  
  // Quick check using bounds
  if (!bounds.contains(point)) return false;
  
  // Ray casting algorithm for point-in-polygon detection
  let isInside = false;
  polygon.getPaths().forEach(path => {
    const vertices = path.getArray();
    for (let i = 0, j = vertices.length - 1; i < vertices.length; j = i++) {
      if (((vertices[i].lat() > point.lat()) !== (vertices[j].lat() > point.lat())) &&
          (point.lng() < (vertices[j].lng() - vertices[i].lng()) * (point.lat() - vertices[i].lat()) / 
          (vertices[j].lat() - vertices[i].lat()) + vertices[i].lng())) {
        isInside = !isInside;
      }
    }
  });
  return isInside;
};

/**
 * Calculate the optimal circle center and radius for a polygon
 * @param polygon The polygon to calculate for
 * @returns An object containing the center and radius in meters
 */
export const calculateOptimalCircleForPolygon = (
  polygon: google.maps.Polygon
): { 
  center: google.maps.LatLng; 
  radiusInMeters: number 
} => {
  if (!polygon) {
    return { 
      center: new google.maps.LatLng(0, 0), 
      radiusInMeters: 1000 
    };
  }
  
  // Extract polygon points
  const latlngs: google.maps.LatLng[] = [];
  const points: {x: number, y: number}[] = [];
  
  polygon.getPaths().forEach(path => {
    path.forEach(point => {
      latlngs.push(point);
      points.push({
        x: point.lng(), // For simplicity using lng as x
        y: point.lat()  // and lat as y
      });
    });
  });
  
  // Find the average center of all points
  let centerX = 0;
  let centerY = 0;
  
  for (const point of points) {
    centerX += point.x;
    centerY += point.y;
  }
  
  centerX /= points.length;
  centerY /= points.length;
  
  // Find the furthest point from this center
  let maxDist = 0;
  let furthestPoint = points[0];
  
  for (const point of points) {
    const dist = Math.sqrt(
      Math.pow(point.x - centerX, 2) + 
      Math.pow(point.y - centerY, 2)
    );
    if (dist > maxDist) {
      maxDist = dist;
      furthestPoint = point;
    }
  }
  
  // Find a second point furthest from the first furthest point
  let secondFurthestPoint = points[0];
  maxDist = 0;
  
  for (const point of points) {
    const dist = Math.sqrt(
      Math.pow(point.x - furthestPoint.x, 2) + 
      Math.pow(point.y - furthestPoint.y, 2)
    );
    if (dist > maxDist) {
      maxDist = dist;
      secondFurthestPoint = point;
    }
  }
  
  // Use the midpoint of these two extreme points as our circle center
  const midX = (furthestPoint.x + secondFurthestPoint.x) / 2;
  const midY = (furthestPoint.y + secondFurthestPoint.y) / 2;
  
  // Calculate final center as LatLng
  const center = new google.maps.LatLng(midY, midX);
  
  // Find the maximum distance from this center to any polygon point
  let maxDistanceInMeters = 0;
  
  for (const latlng of latlngs) {
    const dist = google.maps.geometry.spherical.computeDistanceBetween(center, latlng);
    maxDistanceInMeters = Math.max(maxDistanceInMeters, dist);
  }
  
  // Add a 5% buffer to account for Earth's curvature
  const radiusInMeters = Math.round(maxDistanceInMeters * 1.05);
  
  return { center, radiusInMeters };
};

/**
 * Filters properties to only include those inside a polygon
 * @param properties Array of properties to filter
 * @param polygon The polygon to check against
 * @returns Array of properties that are inside the polygon
 */
export const filterPropertiesInPolygon = (
  properties: any[], 
  polygon: google.maps.Polygon
): any[] => {
  if (!polygon || !properties.length) return [];
  
  return properties.filter(property => {
    // Get lat/lng from property data with proper type conversion
    let lat = 0;
    let lng = 0;
    
    if (typeof property.lat === 'number') {
      lat = property.lat;
    } else if (property.latitude) {
      lat = parseFloat(String(property.latitude));
    }
    
    if (typeof property.lng === 'number') {
      lng = property.lng;
    } else if (property.longitude) {
      lng = parseFloat(String(property.longitude));
    }
    
    // Skip invalid coordinates
    if (isNaN(lat) || isNaN(lng)) return false;
    
    // Create a LatLng object for the property
    const propertyLatLng = new google.maps.LatLng(lat, lng);
    
    // Check if the property is inside the polygon
    return isPointInPolygon(propertyLatLng, polygon);
  });
};

/**
 * Calculate the minimum bounding circle for a polygon.
 * This is an improved version of the calculateOptimalCircleForPolygon function
 * that provides a tighter-fitting circle around the polygon.
 * 
 * @param polygon The polygon to calculate the circle for
 * @returns The center and radius of the minimum bounding circle
 */
export const calculateMinimumBoundingCircle = (
  polygon: google.maps.Polygon
): { 
  center: google.maps.LatLng; 
  radiusInMeters: number;
  displayRadiusInMeters: number;
} => {
  if (!polygon) {
    return { 
      center: new google.maps.LatLng(0, 0), 
      radiusInMeters: 1000,
      displayRadiusInMeters: 900
    };
  }
  
  // Create a bounds object
  const bounds = new google.maps.LatLngBounds();
  
  // Collect all points and extend the bounds
  const points: google.maps.LatLng[] = [];
  polygon.getPaths().forEach(path => {
    path.forEach(point => {
      points.push(point);
      bounds.extend(point);
    });
  });
  
  // Get the center of the bounds
  const center = bounds.getCenter();
  
  // Find the maximum distance from the center to any vertex
  let maxDistanceInMeters = 0;
  
  for (const point of points) {
    const dist = google.maps.geometry.spherical.computeDistanceBetween(center, point);
    maxDistanceInMeters = Math.max(maxDistanceInMeters, dist);
  }
  
  // Add a small buffer (5%) to ensure all points are included due to Earth's curvature
  const radiusInMeters = Math.round(maxDistanceInMeters * 1.05);
  
  // For display purposes, we can use a slightly smaller radius (95% of the search radius)
  // This makes the circle look visually tighter while still including all points in the search
  const displayRadiusInMeters = Math.round(radiusInMeters * 0.95);
  
  return { 
    center, 
    radiusInMeters,
    displayRadiusInMeters
  };
}; 